import { useState, useEffect, useCallback } from 'react';
import { timeTrackingApi } from '../services/timeTrackingApi';

const useOfflineSync = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingUpdates, setPendingUpdates] = useState([]);

  // Load pending updates from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('pendingTrackingUpdates');
    if (saved) {
      try {
        setPendingUpdates(JSON.parse(saved));
      } catch (error) {
        console.error('Error loading pending updates:', error);
        localStorage.removeItem('pendingTrackingUpdates');
      }
    }
  }, []);

  // Save pending updates to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('pendingTrackingUpdates', JSON.stringify(pendingUpdates));
  }, [pendingUpdates]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Connection restored');
      setIsOnline(true);
    };

    const handleOffline = () => {
      console.log('📡 Connection lost - switching to offline mode');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Sync pending updates when coming back online
  useEffect(() => {
    if (isOnline && pendingUpdates.length > 0) {
      syncPendingUpdates();
    }
  }, [isOnline]);

  const addPendingUpdate = useCallback((updateData) => {
    const update = {
      id: Date.now() + Math.random(),
      timestamp: Date.now(),
      data: updateData,
      attempts: 0
    };
    
    console.log('📝 Adding pending update:', update);
    setPendingUpdates(prev => [...prev, update]);
  }, []);

  const syncPendingUpdates = useCallback(async () => {
    if (pendingUpdates.length === 0) return;

    console.log(`🔄 Syncing ${pendingUpdates.length} pending updates...`);
    
    const successful = [];
    const failed = [];

    for (const update of pendingUpdates) {
      try {
        console.log('📤 Sending update:', update.data);
        
        if (update.data.action === 'update') {
          await timeTrackingApi.updateTracking(
            update.data.session_id,
            update.data.total_seconds,
            update.data.active_seconds,
            update.data.idle_seconds
          );
        } else if (update.data.action === 'stop') {
          await timeTrackingApi.stopTracking(update.data.session_id);
        }
        
        successful.push(update.id);
        console.log('✅ Update synced successfully');
      } catch (error) {
        console.error('❌ Failed to sync update:', error);
        update.attempts = (update.attempts || 0) + 1;
        
        // Remove after 3 failed attempts
        if (update.attempts >= 3) {
          console.log('🗑️ Removing update after 3 failed attempts');
          failed.push(update.id);
        }
      }
    }

    // Remove successful and permanently failed updates
    setPendingUpdates(prev => 
      prev.filter(update => 
        !successful.includes(update.id) && !failed.includes(update.id)
      )
    );

    if (successful.length > 0) {
      console.log(`✅ Successfully synced ${successful.length} updates`);
    }
  }, [pendingUpdates]);

  const queueTrackingUpdate = useCallback((sessionId, totalSeconds, activeSeconds, idleSeconds) => {
    const updateData = {
      action: 'update',
      session_id: sessionId,
      total_seconds: totalSeconds,
      active_seconds: activeSeconds,
      idle_seconds: idleSeconds
    };

    if (isOnline) {
      // Try to send immediately if online
      timeTrackingApi.updateTracking(sessionId, totalSeconds, activeSeconds, idleSeconds)
        .then(() => {
          console.log('📤 Tracking update sent successfully');
        })
        .catch((error) => {
          console.error('📤 Failed to send tracking update, queuing for later:', error);
          addPendingUpdate(updateData);
        });
    } else {
      // Queue for later if offline
      console.log('📡 Offline - queuing tracking update');
      addPendingUpdate(updateData);
    }
  }, [isOnline, addPendingUpdate]);

  const queueStopTracking = useCallback((sessionId) => {
    const stopData = {
      action: 'stop',
      session_id: sessionId
    };

    if (isOnline) {
      // Try to send immediately if online
      return timeTrackingApi.stopTracking(sessionId)
        .catch((error) => {
          console.error('📤 Failed to stop tracking, queuing for later:', error);
          addPendingUpdate(stopData);
          throw error;
        });
    } else {
      // Queue for later if offline
      console.log('📡 Offline - queuing stop tracking');
      addPendingUpdate(stopData);
      return Promise.resolve();
    }
  }, [isOnline, addPendingUpdate]);

  return {
    isOnline,
    pendingUpdates: pendingUpdates.length,
    queueTrackingUpdate,
    queueStopTracking,
    syncPendingUpdates
  };
};

export default useOfflineSync;